// ES Link Flangoo Test Login Creds:
// flangoostudent@example.com
// flangooteacher@example.com
// PW: FlangooTest
// Both enrolled in Flangoo Test Course 101

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { Link, useParams, useLocation, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  getRedirectResult,
} from "firebase/auth";

import { LoaderBar, LoaderDots } from "../../../middleware/loaders";
import { LOGIN_MUTATION, STUDENT_MUTATION } from "./queries";
import { ErrorType } from "../../../models/errors";
import {
  smartRedirect,
  validateEmail,
} from "../../../middleware/common-functions";
import { SsoButton } from "../../common/buttons";
import {
  setAuthCookie,
  getAuthCookie,
  removeAuthCookie,
} from "../../../middleware/cookie-manager";
import { PasswordInput } from "../../common/icons";
import LoginBackground from "./background";
const provider = new GoogleAuthProvider();
const auth = getAuth();

const LoginPage: React.FC = () => {
  const time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const email = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const history = useHistory();
  const { autologin_token, type, page } = useParams<{
    autologin_token: string;
    type?: string;
    page?: string;
  }>();
  ///get values from url params
  const query = useQuery();
  const [error, setError] = useState<ErrorType | null>(null);
  const [loginStep, setLoginStep] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [userType, setUserType] = useState<"Teacher" | "Student">("Teacher");
  const password = useRef<HTMLInputElement>(null);
  const [
    login,
    { data: login_data, loading: loading_user, error: loginError },
  ] = useMutation(LOGIN_MUTATION);
  const [
    student_login,
    {
      data: student_login_data,
      loading: loading_student,
      error: loginErrorStudent,
    },
  ] = useMutation(STUDENT_MUTATION);

  useEffect(() => {
    const code = query.get("code");
    const error_url = query.get("error");
    const error_description = query.get("error_description");
    const response_type = query.get("response_type");
    const set_user_type =
      location.pathname === "/signin" ? "Teacher" : "Student";

    if (code) {
      console.log("index.tsx:150 | code", code);
      // Determine the SSO type based on state parameter or referrer
      let ssoType: "firebase" | "edlink" | "classlink" = "edlink";

      if (response_type && response_type.includes("code")) {
        ssoType = "classlink";
      } else if (document.referrer && document.referrer.includes("classlink")) {
        ssoType = "classlink";
      }

      loginFunction(code, ssoType, set_user_type);
    } else if (error_url && error_description) {
      const set_error: ErrorType = {
        message: error_description,
      };
      setError(set_error);
    }
    ///login with Firebase on refresh
    getRedirectResult(auth)
      .then((result) => {
        if (!result) {
          return;
        }
        const user = result.user;

        //@ts-ignore next-line
        const firebase_token = user.accessToken;
        if (firebase_token) {
          loginFunction(firebase_token, "firebase", set_user_type);
        } else {
          setError({
            message: "Something went wrong. Please try again.",
          });
        }
      })
      .catch((error) => {
        // Handle Errors here.
        const errorCode = error.code;
        const errorMessage = error.message;
        setError({
          message: "Something went wrong. Please try again.",
          reason: `${errorMessage} | Code: ${errorCode}`,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    const login_error = localStorage.getItem("logoutType");
    const set_user_type =
      location.pathname === "/signin" ? "Teacher" : "Student";
    setUserType(set_user_type);
    if (login_error) {
      setError({
        message: login_error,
      });
    }
    if (autologin_token) {
      autologinHandler();
    }

    localStorage.removeItem("logoutType");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (loading_student || loading_user) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [loading_student, loading_user]);

  useEffect(() => {
    if (login_data && login_data.login) {
      const user = login_data.login;
      if (user.admin === 0) {
        delete user.admin;
      }
      setAuthCookie(user);

      // Get the intended path from localStorage
      const intendedPath = localStorage.getItem("intendedPath");

      if (type) {
        switch (type) {
          case "reset":
            window.location.href = "/editprofile/reset";
            break;
          case "redirect":
            window.location.href = intendedPath || "/";
            break;
          default:
            window.location.href = intendedPath || "/";
            break;
        }
      } else {
        window.location.href = intendedPath || "/";
      }

      // Clear the stored path
      localStorage.removeItem("intendedPath");
    }
  }, [login_data, type]);

  useEffect(() => {
    const inputField = document.getElementById("login_data");
    if (loginError) {
      const set_error: ErrorType = {
        message: loginError.graphQLErrors[0].message,
      };
      setError(set_error);
      inputField?.focus();
    } else if (loginErrorStudent) {
      const set_error: ErrorType = {
        message: loginErrorStudent.graphQLErrors[0].message,
      };
      setError(set_error);
      inputField?.focus();
    }
  }, [loginError, loginErrorStudent]);

  useEffect(() => {
    if (student_login_data && student_login_data.studentLogin) {
      if (
        student_login_data.studentLogin.__typename === "StudentCodeResponse"
      ) {
        if (student_login_data.studentLogin.valid) {
          setLoginStep(2);
          return;
        }
      }
      const user = student_login_data.studentLogin;

      const password_val = password.current?.value;
      user.type = "student";
      user.is_teacher = false;
      setAuthCookie(user);

      // Get the intended path from localStorage
      const intendedPath = localStorage.getItem("intendedPath");
      // Clear the stored path
      localStorage.removeItem("intendedPath");
      if (password_val === "changeme") {
        window.location.href = "/student";
      } else {
        window.location.href = intendedPath || "/";
      }
    }
  }, [student_login_data]);

  const loginHandler = async () => {
    const email_val = email.current!.value;
    setError(null);
    if (loginStep === 1 && userType === "Student") {
      if (validateEmail(email_val)) {
        setLoginStep(2);
        return;
      } else {
        const { data, errors } = await student_login({
          variables: {
            login_code: email_val,
            timezone: time_zone,
          },
        });
        const result = data?.studentLogin;
        if (errors) {
          setError({
            message: errors[0].message,
          });
          return;
        }
        if (result?.__typename === "StudentCodeResponse") {
          const { valid } = result;
          if (valid) {
            setLoginStep(2);
          } else {
            setError({
              message: "Invalid student login code.",
            });
          }
        }
        return;
      }
    }
    const password_val = password.current!.value;
    if (validateEmail(email_val)) {
      try {
        await login({
          variables: {
            email: email_val,
            pass: password_val,
            timezone: time_zone,
          },
        });
      } catch (error) {
        console.log("index.tsx:53 | error", error);
      }
    } else {
      try {
        await student_login({
          variables: {
            login_code: email_val,
            pass: password_val,
            timezone: time_zone,
          },
        });
      } catch (error) {
        console.log("index.tsx:53 | error", error);
      }
    }
  };

  const autologinHandler = async () => {
    const user = getAuthCookie();

    if (user?.token === autologin_token) {
      const redirect_to = page ? smartRedirect(page) : false;
      window.location.href = redirect_to || "/";
      return;
    } else if (user?.token) {
      removeAuthCookie();
    }
    try {
      await login({ variables: { token: autologin_token } });
    } catch (error) {
      history.push({
        pathname: `/signin`,
      });
    }
  };

  const enterPressed = (ev: React.KeyboardEvent): void => {
    var code = ev.keyCode || ev.which;
    if (code === 13) {
      //13 is the enter keycode
      loginHandler();
    }
  };

  const handleGoogleSignup = async (
    e: React.ChangeEvent<HTMLButtonElement>,
  ) => {
    signInWithPopup(auth, provider)
      .then((result) => {
        const user = result.user;
        //@ts-ignore next-line
        const firebase_token = user.accessToken;

        if (firebase_token) {
          loginFunction(firebase_token, "firebase", userType);
        } else {
          setError({
            message: "Something went wrong. Please try again.",
          });
        }
      })
      .catch((error) => {
        // Handle Errors here.
        const errorCode = error.code;
        const errorMessage = error.message;
        setError({
          message: "Something went wrong. Please try again.",
          reason: `${errorMessage} | Code: ${errorCode}`,
        });
      });
  };
  const handleEdlinkSignup = async (
    e: React.ChangeEvent<HTMLButtonElement>,
  ) => {
    const loginUrl = `https://ed.link/sso/login?client_id=9aabc0db-fdeb-43e7-aadd-028467a96ac9&redirect_uri=${process.env.REACT_APP_URL}${location.pathname}`;
    window.location.href = loginUrl;
  };

  const handleClasslinkSignup = (app_id: string) => {
    localStorage.setItem("app_id", app_id);
    const clientId = process.env.REACT_APP_CLASS_LINK_CLIENT_ID;
    const redirectUri = `${process.env.REACT_APP_URL}${location.pathname}`;
    const loginUrl = `https://launchpad.classlink.com/oauth2/v2/auth?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri,
    )}&scope=profile&state=teacher_applications`;
    window.location.href = loginUrl;
  };

  const loginFunction = async (
    firebase_token: string,
    type: "firebase" | "edlink" | "classlink" = "firebase",
    set_user_type: "Student" | "Teacher" = "Teacher",
  ) => {
    if (set_user_type === "Teacher") {
      try {
        await login({
          variables: { timezone: time_zone, firebase_token, token_type: type },
        });
      } catch (error) {
        // history.push({
        //   pathname: `/signin`,
        // });
      }
    } else {
      try {
        await student_login({
          variables: { timezone: time_zone, firebase_token, token_type: type },
        });
      } catch (error) {
        console.log("index.tsx:53 | error", error);
      }
    }
  };
  const renderSSOButtons = () => {
    if (location.pathname === "/google") {
      return (
        <SsoButton
          txt="Log in"
          fun={handleEdlinkSignup}
          type="Google Classroom"
        />
      );
    }
    return (
      <>
        <SsoButton
          txt="Log in"
          fun={handleEdlinkSignup}
          type="Google Classroom"
        />
        <SsoButton txt="Log in" fun={handleEdlinkSignup} type="Schoology" />
        <SsoButton txt="Log in" fun={handleEdlinkSignup} type="Canvas" />
        <SsoButton txt="Log in" fun={handleClasslinkSignup} type="ClassLink" />
        {userType === "Teacher" ? null : (
          <SsoButton txt="Log in" fun={handleGoogleSignup} type="Google" />
        )}
      </>
    );
  };

  const passwordContent = (
    <div className={` ${autologin_token ? "d_none" : ""}`}>
      <label className="text-white" htmlFor="password_input">
        Password
      </label>
      <PasswordInput
        id="password_input"
        placeholder="Password"
        // handleType={handleType}
        data_type="password"
        onKeyPress={enterPressed}
        refProp={password}
      />
    </div>
  );

  const authContent = (
    <div className={`form-group ${autologin_token ? "d_none" : ""}`}>
      <label className="text-white" id="login_data_label" htmlFor="login_data">
        {userType === "Teacher" ? `Enter email` : "Enter Student Login Code"}
      </label>
      <input
        type="text"
        name="login_data"
        className="form-control"
        id="login_data"
        aria-required="true"
        ref={email}
        placeholder={
          userType === "Teacher" ? `Teacher Email` : "Student Login Code"
        }
        onKeyPress={enterPressed}
      />
    </div>
  );
  return (
    <main
      className={`signin-page ${userType === "Student" ? "student_view" : ""}`}
    >
      <Helmet title={`${userType} Sign-In`} />
      {loading ? <LoaderBar fixed /> : null}
      <LoginBackground />
      <div className="container login_div">
        <div className="row form_cont  min-vh-100">
          <div className="col-md-4">
            <div className="row text-center justify-content-center">
              <div className="col-12 my-3">
                <h1 className="text-capitalize">{userType} Sign-In</h1>
              </div>
            </div>
            {userType === "Teacher" ? (
              <>
                <SsoButton
                  txt="Log in"
                  fun={handleGoogleSignup}
                  type="Google"
                />
                <p className="text-center my-2 text-muted">
                  <small>Or</small>
                </p>
              </>
            ) : null}

            {authContent}
            {loginStep === 2 || userType === "Teacher" ? passwordContent : null}

            {error && !loading_user ? (
              <div className="alert alert-warning">
                <i
                  className="fas fa-exclamation-triangle shake_anim"
                  aria-hidden="true"
                ></i>{" "}
                <span
                  dangerouslySetInnerHTML={{ __html: error.message }}
                ></span>
              </div>
            ) : null}
            <button
              type="button"
              id="login-button"
              className="btn btn-raised btn-danger btn-block btn_xs_lg py-3"
              disabled={loading}
              onClick={loginHandler}
            >
              {loading ? (
                <LoaderDots
                  // size="15"
                  color="#333a40"
                  centered={false}
                  display="p-0"
                />
              ) : loginStep === 1 && userType === "Student" ? (
                "Next"
              ) : (
                "Sign-In"
              )}
            </button>
            {!autologin_token ? (
              <p>
                {userType === "Teacher" ? (
                  <small className="d-flex justify-content-around mt-3">
                    <Link to="/forget">Forgot Password?</Link> |
                    <Link to="/signup">Sign-Up</Link> |
                    <Link to="/student_signin">Student Sign-in</Link>
                  </small>
                ) : (
                  <small className="d-flex justify-content-around mt-3">
                    <Link to="/signin">Teacher Sign-in</Link>
                  </small>
                )}
              </p>
            ) : null}
          </div>
          <div className="col-md-1"></div>
          <div className="col-10 col-sm-5 col-lg-4">
            <div className="row text-center justify-content-center">
              <div className="col-12 my-3">
                <h1 className="text-capitalize">LMS Log In</h1>
              </div>
            </div>
            {userType === "Teacher" ? (
              <p className="text-center text-muted small">
                Logging in with these buttons will work only after your LMS
                administrator has linked your account <br /> to Flangoo{" "}
                <a
                  href="https://ed.link/integrate/9aabc0db-fdeb-43e7-aadd-028467a96ac9"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>
            ) : null}
            {renderSSOButtons()}
          </div>
        </div>
      </div>
    </main>
  );
};

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

export default LoginPage;
